export const PATTERNS = {
  "medallion-spine": {
    overline: "Pattern Study",
    title: "Same Spine.",
    accent: "Two Budgets.",
    subtitle:
      "One medallion architecture serves the legal budget and the P&L budget. You do not sell a lakehouse — you sell a number that traces. Medallion is the machine that makes that true.",
    diagramTitle: "The Medallion Spine",
    diagram: `graph TD
    subgraph Spine ["The Medallion Spine"]
        A[Enterprise Sources] --> B["Bronze — raw, immutable"]
        B --> C["Silver — cleaned, conformed"]
        C --> D["Gold — business-ready"]
    end
    E["OpenLineage Events → Customer's Catalog"] -.->|Lineage evidence| C
    E -.->|End-to-end audit lineage| D
    D --> F[Filings · KPIs · AI Products]`,
    compareTitle: "Two Verticals, One Architecture",
    columns: [
      { name: "ESG", note: "Legal & Sustainability Budget", slug: "esg-data-governance-medallion-audit-lineage", linkLabel: "Blueprint: ESG Data Governance" },
      { name: "Trade Promotion", note: "Trade-Spend P&L", slug: "ai-trade-promotion-optimization", linkLabel: "Blueprint: Trade Promotion Optimization" },
    ],
    rows: [
      { label: "The Budget", values: [
        "Legal & sustainability — regulatory assurance spend",
        "Trade-spend P&L — the second-largest line, defended to the CFO",
      ]},
      { label: "The Buyer", values: [
        "Sustainability officer, general counsel, audit committee",
        "CFO, VP Sales, revenue operations",
      ]},
      { label: "Sources", values: [
        "Facility energy meters, Scope 1–3 supplier data",
        "SAP ECC, Oracle EBS, Salesforce, retailer POS",
      ]},
      { label: "Bronze", values: [
        "Raw emissions telemetry, append-only",
        "Raw transaction events, append-only",
      ]},
      { label: "Silver", values: [
        "Scope 1–3 conformed, emission factors applied",
        "Sales Fact, Product Master, Customer Profile",
      ]},
      { label: "Gold", values: [
        "GHG KPIs, AASB S2 filings, investor portals",
        "Uplift & segmentation models, promotion ROI KPIs",
      ]},
      { label: "The Number That Traces", values: [
        "Every figure in the filing traces to a source sensor",
        "Every ROI figure traces to a source transaction",
      ]},
      { label: "The Room That Closes", values: [
        "The audit question — “show me where this number came from” — becomes a click-through demo",
        "CFO budget defense — the ROI story survives scrutiny because every figure is auditable",
      ]},
      { label: "Outcomes", values: [
        "End-to-end in-scope lineage · 60% preparation overhead reduced (engagement-derived)",
        "30% higher promotion ROI (engagement-derived) · 15+ automated business KPIs",
      ]},
    ],
    whyTitle: "Why This Is GTM, Not Data Engineering",
    whyCards: [
      { title: "One Picture, Three Buyers", body: "The engineer sees tiers. The CISO sees lineage and RBAC. The CFO sees Gold KPIs. Same diagram — no translation deck required." },
      { title: "The Audit Question Becomes a Demo", body: "“Show me where this number came from” is a click through Gold → Silver → Bronze — not a six-week evidence pack." },
      { title: "Change the Payload, Keep the Spine", body: "Sources and Gold products swap per vertical; the architecture stays. Ten Blueprints, one machine — that is what makes it transferable." },
      { title: "Light-Touch Delivery", body: "I architect the spine and the lineage contract. Your platform team owns the Spark jobs, the CDC streams, and the catalog." },
    ],
    cta: "If one architecture should be closing two budgets for you —",
    seoTitle: "Same Spine, Two Budgets — The Medallion Pattern | TECC",
    seoDescription:
      "One medallion architecture serving two budgets: ESG regulatory assurance and trade-promotion P&L. The transferable Field CTO pattern.",
  },

  "cdc-deal-closer": {
    overline: "Pattern Study",
    title: "One Stream.",
    accent: "Three Deals.",
    subtitle:
      "Enterprise software deals rarely fail because a product is missing a feature checkbox. They fail during technical validation — when infrastructure, security, data, compliance, and enterprise architecture teams test product claims against production reality.",
    thesis:
      "A governed streaming architecture built on log-based CDC addresses the three objections below — but only when paired with source prerequisites and performance validation, security and retention controls, schema and data-contract management, lineage and observability, reconciliation and rollback procedures, and named operational ownership. The stream is the spine; the control model is the proof.",
    diagramTitle: "The CDC Spine",
    diagram: `graph TD
    subgraph Capture ["Bronze — Passive Log Capture"]
        A[Production Systems] -->|transaction logs, never queries| B[CDC Engine]
    end
    subgraph Stream ["The Change Stream"]
        B -->|change events| C[Kafka / AWS MSK]
    end
    subgraph Medallion ["Medallion Target"]
        C --> D["Silver — conformed"]
        D --> E["Gold — business-ready"]
    end
    F["OpenLineage Events → Customer's Catalog"] -.->|lineage evidence| D
    E --> G[AI Products · Filings · Risk Models]`,
    compareTitle: "Three Verticals, One Stream",
    columns: [
      { name: "Mainframe Liberation", note: "Cost Objection", slug: "mainframe-data-liberation-mips-optimization", linkLabel: "Blueprint: Mainframe & MIPS" },
      { name: "Healthcare Clinical Data", note: "Custody Objection", slug: "healthcare-clinical-data-engine", linkLabel: "Blueprint: Healthcare Clinical Data" },
      { name: "Replication Pre-Flight", note: "Cutover Objection", slug: "replication-risk-scoring-preflight", linkLabel: "Blueprint: Replication Risk Scoring" },
    ],
    rows: [
      { label: "The Buyer", values: [
        "CIO & CFO — mainframe cost is a board line item",
        "CMIO & compliance officer — custody is the gate",
        "VP of data platform — cutover risk owns the roadmap",
      ]},
      { label: "The Fear in the Room", values: [
        "“Don't touch production.” Every analytical query is a MIPS invoice",
        "“Prove the data's custody and lineage.” Audits demand traceable evidence",
        "“Cutover will break us.” Pipelines fail under real write loads",
      ]},
      { label: "The CDC Move", values: [
        "Passive log read of DB2 z/OS — capture from logs, not queries against production tables",
        "Containerized CDC engine on EHR and claims logs — low-latency, lineage-tagged, access-controlled",
        "A 73-point pre-flight index risk-scores the pipeline before it ever runs",
      ]},
      { label: "What the Stream Feeds", values: [
        "PostgreSQL medallion — risk, telematics, and claims products",
        "Silver/Gold feature store with OpenLineage lineage evidence — prior-auth AI",
        "A scored, staged rollout — coverage expanded from 11% to 50%+ of eligible in-scope tables",
      ]},
      { label: "The Number That Closes", values: [
        "Source impact measured and validated before scale-out; MIPS growth halted for in-scope workloads (engagement-derived)",
        "Low-latency ingestion validated under source conditions; 20–40% audit-preparation reduction on in-scope paths (engagement-derived)",
        "Coverage expanded through phased gates with rollback controls; Gartner downtime-cost range used as the prioritization benchmark",
      ]},
      { label: "The Room That Closes", values: [
        "The CFO cost review — the architecture is the sales asset",
        "The audit question becomes a click-through demo",
        "The cutover objection becomes an engineering artifact",
      ]},
    ],
    whyTitle: "Why CDC Closes Deals",
    whyCards: [
      { title: "Non-Invasive by Architecture", body: "CDC reads transaction logs, never production tables. The security and performance objection dissolves before it is raised." },
      { title: "Freshness as a Demo", body: "Sub-second change streams make “real-time” something you show in the room — not something you promise in a deck." },
      { title: "One Wire for Governance and GTM", body: "The same stream feeds lineage; every downstream number traces to source. The deal and the audit ride the same rails." },
      { title: "Light-Touch Delivery", body: "I architect the capture and the contract; your platform team runs the connectors." },
    ],
    disclaimer:
      "CDC alone does not deliver compliance, zero source impact, zero downtime, or universal sub-second latency. Those outcomes come from the control model around the stream — prerequisites, validation, reconciliation, replay, rollback, and named ownership — not from the capture mechanism.",
    checklists: [
      {
        title: "Buyer Readiness Checklist",
        testId: "pattern-checklist-buyer",
        items: [
          "Named data owner and operational owner for each in-scope pipeline",
          "Source prerequisites confirmed: log access, retention windows, capture configuration",
          "Security controls defined: access, encryption, classification, retention, audit logging",
          "Schema-evolution and data-contract process agreed before onboarding",
          "Success criteria and acceptance thresholds written down before the pilot",
        ],
      },
      {
        title: "Pre-Flight Validation Checklist",
        testId: "pattern-checklist-preflight",
        items: [
          "Source impact measured under peak load, reviewed before scale-out",
          "Reconciliation gates passed per wave: row counts, checksums, application checks",
          "Replay and recovery drilled: interrupted capture resumed without data loss",
          "Rollback path tested per wave, not assumed",
          "Cutover rehearsed on a production-representative environment with go/no-go criteria",
        ],
      },
    ],
    cta: "If one stream should be answering three objections for you —",
    seoTitle: "One Stream, Three Deals: How Log-Based CDC Turns Production, Custody, and Cutover Risk Into Enterprise Proof | TECC",
    seoDescription:
      "A governed streaming architecture built on log-based CDC, paired with source validation, security controls, lineage, reconciliation, and rollback — the pattern that answers production, custody, and cutover objections in enterprise data deals.",
  },
};
